import { isLocalhostDevMode } from "@/utils/utils";
import dayjs from "dayjs";
import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { syncAcrossTabs } from "../../app/store/syncAcrossTabs";
import type { UUID } from "../../typings/global";
import { NotificationStatus } from "./enums";
import type { Notification } from "./types";

const EPOCH = new Date(0).toISOString();

interface NotifiedNotificationsState {
  sentAtByAccount: Record<UUID, string>;
  actions: {
    notificationsReceived: (accountId: UUID, notifications: Notification[]) => Notification[];
  };
}

/**
 * Per account, when the newest notification the user was already told about was sent. A toggle back to unseen keeps
 * the notification's sentAt, so it is not announced again.
 */
const useNotifiedNotificationsStore = create<NotifiedNotificationsState>()(
  devtools(
    // Without it, a tab would announce a notification another tab already did as soon as it regains focus and refetches.
    syncAcrossTabs(
      persist(
        (set, get) => ({
          sentAtByAccount: {},
          actions: {
            notificationsReceived: (accountId, notifications) => {
              const newestSentAt = notifications
                .map((notification) => notification.sentAt)
                .filter((sentAt) => sentAt !== undefined)
                .reduce<string | undefined>(
                  (newest, sentAt) => (!newest || dayjs(sentAt).isAfter(newest) ? sentAt : newest),
                  undefined,
                );
              const notifiedSentAt = get().sentAtByAccount[accountId];

              // A new device starts silently from the newest one; an account without any from the epoch, to announce
              // its first.
              if (!notifiedSentAt) {
                set((state) => ({ sentAtByAccount: { ...state.sentAtByAccount, [accountId]: newestSentAt ?? EPOCH } }));

                return [];
              }

              if (!newestSentAt || !dayjs(newestSentAt).isAfter(notifiedSentAt)) {
                return [];
              }

              set((state) => ({ sentAtByAccount: { ...state.sentAtByAccount, [accountId]: newestSentAt } }));

              return notifications.filter(
                (notification) =>
                  notification.status === NotificationStatus.SENT &&
                  notification.sentAt !== undefined &&
                  dayjs(notification.sentAt).isAfter(notifiedSentAt),
              );
            },
          },
        }),
        {
          name: "cliip:notified-notifications",
          storage: createJSONStorage(() => localStorage),
          partialize: (state) => ({ sentAtByAccount: state.sentAtByAccount }),
        },
      ),
    ),
    { name: "notifiedNotifications", enabled: isLocalhostDevMode() },
  ),
);

export const useNotifiedNotificationsActions = () => useNotifiedNotificationsStore((state) => state.actions);
